// Layout.jsx — CONTAINER da casca (dono da lógica). Monta { vm, actions } e renderiza a
// LayoutView (View pura do Claude Design, formato View/ViewModel). Lógica portada VERBATIM
// do Layout legado: flags de menu, labels/ícones por tema,
// prefs de e-mail, tickets, edição de nome, exclusão de conta, deep-links de notificação,
// toasts e tracking. O colapsável de configurações da sidebar virou o Dialog "Configurações"
// (engrenagem no header da LayoutView). NotificationCenter real entra via slot headerExtras.
import { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useMediaQuery } from '@mui/material';
import { authService } from '../services/authService';
import { useCloseOnBack } from '../hooks/useCloseOnBack';
import whatsappService from '../services/whatsappService';
import { useMeter } from '../hooks/useEntitlements';
import usersService from '../services/usersService';
import useDocumentosDaConta from '../hooks/useDocumentosDaConta';
import { isDevEnvironment, isPlansSaleEnabled, isIntegrationsEnabled } from '../utils/environment';
import { useDomainAccess } from '../hooks/useDomainAccess';
import DeleteAccountDialog from './DeleteAccountDialog';
import NotificationCenter from './NotificationCenter';
import NotificationDetailModal from './NotificationDetailModal';
import NotificationErrorBoundary from './NotificationErrorBoundary';
import NotificationToast from './NotificationToast';
import AnnouncementBanner from './AnnouncementBanner';
import { useNotificationToast } from '../hooks/useNotificationToast';
import { useThemeLabels } from '../hooks/useThemeLabels';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { useNotificationCenter } from '../contexts/NotificationCenterContext';
import { trackPageView, trackEvent } from '../utils/tracking';
import { navManifest } from '../../design/nav.manifest.mjs';
import LayoutView from '../shell/LayoutView';
import SettingsView from '../shell/SettingsView';
import WhatsAppInboxView from '../shell/WhatsAppInboxView';
import WhatsAppInbox from './WhatsAppInbox';
import { INBOX_UNREAD_QUERY_KEY } from './whatsappInboxQueryKeys';
import { unreadLabel as inboxUnreadLabel } from './whatsappInboxVmBuilder';
import useWhatsAppInboxIntro from '../hooks/useWhatsAppInboxIntro';

// Conversas do WhatsApp (layout.vm @v9): o segmento de conversas na pílula do chip de créditos abre o painel
// (WhatsAppInboxView, Claude Design — import de 22/10/2026). Desligar a feature sem deploy de API: trocar para false
// (o segmento some e o container não é montado). Plano: docs/PLANO-EXECUCAO-CONVERSAS-WHATSAPP.md, TODO 4.9.
const INBOX_VIEW_READY = true;
const INBOX_VIEW = WhatsAppInboxView;

export default function Layout() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const { openNotificationCenter } = useNotificationCenter();
  const [deepLinkNotificationId, setDeepLinkNotificationId] = useState(null);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [loadingEmailPref, setLoadingEmailPref] = useState(false);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [editNameLoading, setEditNameLoading] = useState(false);
  const [editNameError, setEditNameError] = useState('');
  const [displayName, setDisplayName] = useState(null);
  // CPF/CNPJ da conta + dados de cobrança (settings.vm @v3) — busca ao abrir as Configurações.
  const documentos = useDocumentosDaConta();
  const { carregar: carregarDocumentos } = documentos;

  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = authService.getUserInfo();
  const { getLabel, getIcon } = useThemeLabels();
  const { availableThemes, themeId, changeTheme, loading: themeLoading, colorMode, toggleColorMode } = useThemeCustom();
  const { activeToasts, enqueue: enqueueToast, dismiss: dismissToast, pauseTimer, resumeTimer } = useNotificationToast();

  // Load email notification preference
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await usersService.getNotificationPreferences();
        setEmailNotificationsEnabled(prefs.emailNotificationsEnabled);
      } catch (error) {
        console.warn('[Layout] Could not load notification preferences:', error);
      }
    };
    loadPreferences();
  }, []);

  // Sempre fresco ao abrir: a compra pode ter gravado a cobrança desde a última vez.
  useEffect(() => {
    if (settingsOpen) carregarDocumentos();
  }, [settingsOpen, carregarDocumentos]);

  // Handle email notifications toggle (contrato: recebe o boolean, não o event)
  const handleEmailNotificationsToggle = useCallback(async (newValue) => {
    setLoadingEmailPref(true);
    try {
      await usersService.updateNotificationPreferences(newValue);
      setEmailNotificationsEnabled(newValue);
      trackEvent('email_notifications_toggle', { enabled: newValue });
    } catch (error) {
      console.error('[Layout] Failed to update notification preferences:', error);
    } finally {
      setLoadingEmailPref(false);
    }
  }, []);

  const handleEditNameOpen = () => {
    setEditNameValue(displayName || userInfo?.name || '');
    setEditNameError('');
    setEditNameOpen(true);
  };

  const handleEditNameSave = useCallback(async () => {
    const trimmed = editNameValue.trim();
    if (!trimmed) {
      setEditNameError('O nome não pode ser vazio');
      return;
    }
    if (trimmed.length > 255) {
      setEditNameError('Nome muito longo (máximo 255 caracteres)');
      return;
    }
    setEditNameLoading(true);
    setEditNameError('');
    try {
      await usersService.updateName(trimmed);
      authService.setLocalName(trimmed);
      setDisplayName(trimmed);
      setEditNameOpen(false);
      trackEvent('user_name_updated');
    } catch (error) {
      console.error('[Layout] Failed to update name:', error);
      setEditNameError('Erro ao salvar o nome. Tente novamente.');
    } finally {
      setEditNameLoading(false);
    }
  }, [editNameValue]);

  // Check for notification deep-link querystring (from email click)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openNotifications') === 'true') {
      openNotificationCenter();
      const cleanUrl = location.pathname;
      window.history.replaceState(null, '', cleanUrl);
    }
  }, [location]);

  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/^#notification=(.+)$/);
    if (match) {
      setDeepLinkNotificationId(match[1]);
      window.history.replaceState(null, '', location.pathname + location.search);
    }
  }, [location]);

  // Track page views on navigation
  useEffect(() => {
    const pageName = location.pathname.replace(/^\//, '') || 'dashboard';
    trackPageView(pageName);
  }, [location.pathname]);

  const handleLogout = () => {
    trackEvent('admin_logout', { user_id: userInfo?.sub || 'unknown' });
    // logout() — não redirectToLogin(). O segundo só reautentica: o Login, com a sessão viva,
    // devolveria um JWT novo na hora e o "Sair" não sairia de nada.
    authService.logout();
  };

  const handleMenuClick = (path) => {
    trackEvent('menu_navigation', { destination: path, from: location.pathname });
    navigate(path);
  };

  // "Central de ajuda" (rodapé do drawer) aciona o mesmo botão do widget flutuante de suporte.
  const handleOpenSupportWidget = useCallback(() => {
    const widgetButton = document.querySelector('.agendario-support-widget-button');
    if (widgetButton && typeof widgetButton.click === 'function') {
      widgetButton.click();
      return;
    }
    console.warn('[Layout] Support widget button not found (.agendario-support-widget-button).');
  }, []);

  // ── nav — FONTE ÚNICA: design/nav.manifest.mjs (mesma lista do build:design).
  // Aqui só se aplica tema (getLabel/getIcon) e resolvem os `conditional` (kill-switches/flags).
  // Domínio próprio: quem libera é o SERVIDOR, conta a conta (204 = não é para este usuário).
  // Enquanto a resposta não chega, `liberado` é false e o item não entra — item de menu que
  // aparece e some é pior que item que demora um instante a aparecer.
  const { liberado: domainSalesLiberado } = useDomainAccess();

  const conditions = {
    plansSale: isPlansSaleEnabled(),        // venda de planos oculta por padrão (VITE_FEATURE_PLANS_SALE)
    integrations: isIntegrationsEnabled(),  // WhatsApp liberado p/ todos; kill-switch = VITE_FEATURE_INTEGRATIONS='false'
    domainSales: domainSalesLiberado,       // gate por conta, vindo da API — ver useDomainAccess
    devOnly: isDevEnvironment(),
    // /billing (plano 19) é destino de CTA, não item de menu — entra no manifest só para o par
    // vm/mock chegar ao design-package. Vira true quando for a área de compras completa.
    billingMenu: false,
    customFieldsMenu: true,
  };
  const navItems = navManifest
    .filter((i) => !i.conditional || conditions[i.conditional])
    .map((i) => {
      // getLabel ecoa a chave quando o tema não tem o label → o fallback é o 2º arg (manifest).
      // getIcon devolve o sentinela HelpOutline quando não acha → cai pro ícone do manifest.
      const themeIcon = getIcon(i.key);
      return {
        key: i.key,
        label: getLabel(i.key, i.label),
        iconName: themeIcon?.name && themeIcon.name !== 'HelpOutline' ? themeIcon.name : i.iconName,
        route: i.route,
        migrated: true, // no Admin todas as rotas existem — sem "em breve"
        tag: i.tag ?? null,
      };
    });

  // ── Chip WhatsApp do header (layout.vm @v5) ─────────────────────────────────
  // visible = mesmo kill-switch do menu Integrações; 'disabled' = integração desligada
  // nas settings do dono; saldo/low vêm do snapshot de entitlements (useMeter, que já
  // tem polling de 60s — ver useEntitlements.js).
  const integrationsOn = conditions.integrations;
  const { balance: waBalance, low: waLow, lost: waLostRaw, loading: waMeterLoading } = useMeter('whatsapp_messages');
  // @v8: mensagens barradas por falta de saldo DESDE O ÚLTIMO CRÉDITO (zera sozinho na recarga).
  // Campo novo do snapshot de entitlements — front antigo contra API nova, ou vice-versa, cai em 0.
  const waLost = Number(waLostRaw ?? 0);
  const { data: waSettings, isLoading: waSettingsLoading } = useQuery({
    queryKey: ['whatsapp-settings'],
    queryFn: () => whatsappService.getSettings(),
    enabled: integrationsOn,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  // 'losing' (@v8) exige saldo zerado E perda registrada: é "acabou E já custou cliente". Perda com
  // saldo sobrando existe (bug do SKIP LOCKED, 14/09) mas ali recarregar não conserta nada — alarmar
  // pedindo compra seria pedir dinheiro pelo motivo errado. Esse caso continua 'ok'/'low' e aparece
  // no sino e no relatório, que é onde ele tem conserto.
  const waStatus = (waMeterLoading || waSettingsLoading) ? 'loading'
    : !waSettings?.enabled ? 'disabled'
    : (waBalance <= 0 && waLost > 0) ? 'losing'
    : waBalance <= 0 ? 'depleted'
    : waLow ? 'low'
    : 'ok';
  // Em 'losing' o chip mostra o PREJUÍZO no lugar do saldo — o saldo é 0 e dizer "0" não informa
  // nada; "-3" diz que três clientes ficaram sem aviso.
  const waCreditsLabel = (waStatus === 'loading' || waStatus === 'disabled' || waStatus === 'losing')
    ? null
    : waBalance.toLocaleString('pt-BR');
  const waLostLabel = waStatus === 'losing' ? `-${waLost.toLocaleString('pt-BR')}` : null;
  const waAlertLabel = waStatus === 'losing'
    ? (waLost === 1
        ? 'Um cliente não recebeu seu aviso — recarregue para voltar a notificar'
        : `${waLost} clientes não receberam seu aviso — recarregue para voltar a notificar`)
    : null;
  const waTooltip = {
    loading: 'Carregando saldo…',
    disabled: 'WhatsApp não configurado — clique para ativar',
    losing: waLost === 1
      ? 'Sem créditos — 1 mensagem não foi enviada. Clique para recarregar'
      : `Sem créditos — ${waLost} mensagens não foram enviadas. Clique para recarregar`,
    depleted: 'Saldo esgotado — envios de WhatsApp pausados',
    low: `Pouco saldo — ${waCreditsLabel} créditos restantes`,
    ok: `WhatsApp ativo — ${waCreditsLabel} créditos`,
  }[waStatus];
  // ── Sino de conversas do WhatsApp (layout.vm @v9) ───────────────────────────
  // Só com o WhatsApp LIGADO (D4). O número é de RECEBIDAS não lidas (D2); polling de 60 s, igual ao saldo.
  const inboxVisible = INBOX_VIEW_READY && integrationsOn && waSettings?.enabled === true;
  const { data: inboxUnread } = useQuery({
    queryKey: INBOX_UNREAD_QUERY_KEY,
    queryFn: () => whatsappService.getInboxUnreadCount(),
    enabled: inboxVisible,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const inboxUnreadCount = Number(inboxUnread?.unread ?? 0);
  // @v10: balão que APONTA para o ícone das conversas — uma vez por navegador e conta, só com o WhatsApp ligado,
  // depois do consentimento LGPD. Some com "Entendi" ou quando a pessoa abre as conversas.
  const inboxIntro = useWhatsAppInboxIntro({ inboxVisible, inboxOpen, userId: userInfo?.sub });
  const inboxVm = {
    visible: inboxVisible,
    unreadCount: inboxUnreadCount,
    unreadLabel: inboxUnreadLabel(inboxUnreadCount),
    tooltip: inboxUnreadCount === 0
      ? 'Conversas do WhatsApp'
      : `Conversas do WhatsApp — ${inboxUnreadCount} não ${inboxUnreadCount === 1 ? 'lida' : 'lidas'}`,
    intro: {
      open: inboxIntro.open,
      title: 'Suas conversas do WhatsApp ficam aqui',
      body: 'Toque neste ícone para ver os avisos que o Agendário enviou aos seus clientes e tudo o que eles responderam.',
      confirmLabel: 'Entendi',
    },
  };

  // Convite do chip (layout.vm @v7). O bônus de 50 créditos é concedido pela API na 1ª ativação
  // (WhatsAppMeController.WelcomeCreditQty) — mesma oferta que a tela de Integrações já mostra.
  const waPromoLabel = waStatus === 'disabled' ? 'Ative o WhatsApp e ganhe 50 créditos grátis' : null;

  // ── ViewModel + actions da casca ─────────────────────────────────────────────
  const userName = displayName || userInfo?.name || 'Usuário';
  const vm = {
    nav: navItems,
    header: {
      appTitle: getLabel('appTitle'),
      appSubtitle: getLabel('appSubtitle'),
      userName,
      userInitials: userName.charAt(0).toUpperCase(),
      notificationsCount: 0, // sino embutido não é usado — NotificationCenter real via headerExtras
      whatsapp: {
        visible: integrationsOn, status: waStatus,
        creditsLabel: waCreditsLabel, lostLabel: waLostLabel, alertLabel: waAlertLabel,
        tooltip: waTooltip, promoLabel: waPromoLabel,
        inbox: inboxVm,
      },
      colorMode, // layout.vm @v6 — padrão vem do tema; o usuário troca pelo botão
    },
    activeRoute: location.pathname,
  };

  const actions = {
    onNavigate: handleMenuClick,
    onLogout: handleLogout,
    onOpenNotifications: openNotificationCenter,
    onOpenSettings: () => setSettingsOpen(true),
    onOpenSupportWidget: handleOpenSupportWidget,
    onToggleColorMode: () => {
      trackEvent('header_color_mode_toggle', { from: colorMode, theme: themeId });
      toggleColorMode();
    },
    onOpenWhatsApp: () => {
      trackEvent('header_whatsapp_chip_click', { status: waStatus });
      // Plano 19: sem saldo (ou acabando) o chip leva DIRETO à compra — é o passo de conversão.
      // Ligado e com saldo, ou desligado, leva a Integrações (configurar / ativar).
      const querCompra = waStatus === 'losing' || waStatus === 'depleted' || waStatus === 'low';
      navigate(querCompra ? '/billing?product=whatsapp&ref=header' : '/integrations');
    },
    onOpenWhatsAppInbox: () => {
      trackEvent('header_whatsapp_inbox_open', { unread: inboxUnreadCount, fromIntro: inboxIntro.open });
      inboxIntro.dismiss('opened');   // abriu = já descobriu; o balão não volta (no-op se já tinha visto)
      setInboxOpen(true);
    },
    onDismissWhatsAppInboxIntro: () => inboxIntro.dismiss('confirm'),
  };

  // NotificationCenter real (sino + badge + painel + SignalR + toasts) no header da View.
  const headerExtras = (
    <NotificationErrorBoundary>
      <NotificationCenter onToast={enqueueToast} />
    </NotificationErrorBoundary>
  );

  // Voltar do celular nas Configurações (tela cheia abaixo de 600px — o mesmo corte da SettingsView): fecha a
  // edição do nome, se aberta; senão fecha as Configurações. Sem isto o voltar saía da tela. utils/backButtonLayers.js
  const settingsTelaCheia = useMediaQuery('(max-width:599px)');
  useCloseOnBack(settingsOpen && settingsTelaCheia, () => {
    if (editNameOpen) {
      if (!editNameLoading) setEditNameOpen(false);
      return true;   // as Configurações continuam abertas
    }
    setSettingsOpen(false);
    return false;
  });

  // ── ViewModel + actions do dialog de Configurações (contrato: shell/settings.vm.js @v4) ──
  const settingsVm = {
    open: settingsOpen,
    userName,
    email: userInfo?.email || '',
    emailNotificationsEnabled,
    savingEmailPref: loadingEmailPref,
    editName: { open: editNameOpen, value: editNameValue, error: editNameError || null, saving: editNameLoading },
    // "Tipo de negócio" = tema do tenant (saiu do header no layout.vm @v3)
    businessType: { currentId: themeId, options: availableThemes, saving: themeLoading },
    documents: documentos.documents,
    editDocuments: documentos.editDocuments,
  };
  const settingsActions = {
    onClose: () => setSettingsOpen(false),
    onToggleEmailNotifications: handleEmailNotificationsToggle,
    onOpenEditName: handleEditNameOpen,
    onChangeEditNameValue: setEditNameValue,
    onSaveEditName: handleEditNameSave,
    onCancelEditName: () => { if (!editNameLoading) setEditNameOpen(false); },
    onOpenTerms: () => window.open('/termos-de-uso', '_blank'),
    onDeleteAccount: () => { setSettingsOpen(false); setDeleteAccountOpen(true); },
    onLogout: handleLogout,   // @v4 — o mesmo "Sair" do header
    onChangeBusinessType: changeTheme,
    ...documentos.actions,
  };

  return (
    <>
      <LayoutView vm={vm} actions={actions} headerExtras={headerExtras}>
        <AnnouncementBanner />
        <Outlet />
      </LayoutView>

      {/* Dialog de Configurações — View do designer (inclui o sub-dialog "Editar nome") */}
      <SettingsView vm={settingsVm} actions={settingsActions} />

      {/* Painel de conversas do WhatsApp — View do designer (whatsappInbox.vm @v1); só monta com a View pronta */}
      {inboxVisible && (
        <WhatsAppInbox open={inboxOpen} onClose={() => setInboxOpen(false)} View={INBOX_VIEW} />
      )}

      {/* Deep link notification modal */}
      <NotificationDetailModal
        open={!!deepLinkNotificationId}
        onClose={() => setDeepLinkNotificationId(null)}
        notificationId={deepLinkNotificationId}
      />

      {/* Toast notifications */}
      <NotificationToast
        toasts={activeToasts}
        onDismiss={dismissToast}
        onPauseTimer={pauseTimer}
        onResumeTimer={resumeTimer}
      />

      {/* Dialog de exclusão de conta */}
      <DeleteAccountDialog
        open={deleteAccountOpen}
        onClose={() => setDeleteAccountOpen(false)}
        userEmail={userInfo?.email}
      />
    </>
  );
}
